import { useEffect, useState, type FormEvent } from "react";
import { refreshReturnTarget } from "../../lib/loginReturn";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { supabase } from "../../lib/supabase";
import { Eye, EyeOff, Check } from "lucide-react";
import "./CompleteSetup.css";

export function CompleteSetupPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  // Setup can take longer than the ten minute target window: restart the clock.
  useEffect(() => {
    refreshReturnTarget();
  }, []);

  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const passwordRequirements = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains a number", met: /\d/.test(password) },
    {
      label: "Passwords match",
      met: password === confirmPassword && password.length > 0,
    },
  ];

  const allRequirementsMet = passwordRequirements.every((r) => r.met);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRequirementsMet) {
      setError("Please meet all password requirements");
      return;
    }

    setLoading(true);

    try {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      const { error: passwordError } = await supabase.auth.updateUser({
        password,
      });

      if (passwordError) throw passwordError;

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          setup_complete: true,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      await refreshProfile();
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-container">
      <div className="setup-card">
        <div className="setup-header">
          <img
            src={branding.logo_url || "/claims-app-logo.svg"}
            alt={branding.company_name || branding.app_name}
            className="setup-logo"
          />
          <h1 className="setup-title">Complete Your Setup</h1>
          <p className="setup-subtitle">
            Set your name and password to continue
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="setup-form-group">
            <label htmlFor="fullName" className="setup-label">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              className="setup-input"
              placeholder="John Smith"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>

          <div className="setup-form-group">
            <label htmlFor="password" className="setup-label">
              New Password
            </label>
            <div className="setup-password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="setup-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                className="setup-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="setup-form-group">
            <label htmlFor="confirmPassword" className="setup-label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              className="setup-input"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <div className="setup-requirements">
            {passwordRequirements.map((req) => (
              <div
                key={req.label}
                className={`setup-requirement ${req.met ? "met" : ""}`}
              >
                <Check size={14} />
                {req.label}
              </div>
            ))}
          </div>

          {error && (
            <div className="setup-error">
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            className="setup-button"
            disabled={loading || !allRequirementsMet}
          >
            {loading ? "Setting up..." : "Complete Setup"}
          </button>
        </form>
      </div>
    </div>
  );
}
